import { useEffect, useMemo, useRef, useState } from 'react';

import {
  useMyFollows,
  useToggleFollowAuthor,
} from '@/features/follows/hooks/useMyFollows.js';
import { useAuthorSuggestions } from '@/features/library/hooks/useAuthorSuggestions.js';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue.js';

import type { UpcomingItem } from '../types.js';

const DATE_FORMAT = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'short',
});

/**
 * Hauteur de la zone scrollable : ~10 lignes d'auteur. Chaque `author-row`
 * fait ~52px (8px padding + 36px avatar). 10 × 52 ≈ 520px → 480 + petit marge.
 */
const SCROLL_MAX_HEIGHT = 480;

interface AuthorsListProps {
  /** Liste d'items upcoming pour résoudre la "prochaine sortie" d'un auteur. */
  readonly entries: ReadonlyArray<UpcomingItem>;
}

/**
 * Carte "Auteurs suivis" — la barre en haut sert à la fois à **filtrer**
 * la liste affichée et à **ajouter** un nouvel auteur (autocomplétion +
 * fallback "Suivre" sur la saisie libre).
 */
export function AuthorsList({ entries }: AuthorsListProps): JSX.Element {
  const { data: follows } = useMyFollows();
  const toggleMutation = useToggleFollowAuthor();
  const [query, setQuery] = useState('');

  const followedSet = useMemo(
    () => new Set((follows?.authors ?? []).map((a) => a.toLowerCase())),
    [follows],
  );

  const nextByAuthor = useMemo(() => {
    const map = new Map<string, { title: string; date: Date | null }>();
    for (const it of entries) {
      const a = it.book.author;
      const date = it.book.releaseDate ? new Date(it.book.releaseDate) : null;
      const existing = map.get(a);
      if (
        !existing ||
        (date && existing.date && date.getTime() < existing.date.getTime())
      ) {
        map.set(a, { title: it.book.title, date });
      }
    }
    return map;
  }, [entries]);

  const followedAuthors = follows?.authors ?? [];

  const suggestions = useMemo(() => {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const it of entries) {
      const a = it.book.author;
      const key = a.toLowerCase();
      if (followedSet.has(key)) continue;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(a);
      if (out.length >= 5) break;
    }
    return out;
  }, [entries, followedSet]);

  // Filtre local : si la saisie contient du texte, on filtre les deux listes.
  const q = query.trim().toLowerCase();
  const filteredFollowed = useMemo(() => {
    if (q.length === 0) return followedAuthors;
    return followedAuthors.filter((a) => a.toLowerCase().includes(q));
  }, [followedAuthors, q]);
  const filteredSuggestions = useMemo(() => {
    if (q.length === 0) return suggestions;
    return suggestions.filter((a) => a.toLowerCase().includes(q));
  }, [suggestions, q]);

  const handleAddRaw = (): void => {
    const trimmed = query.trim();
    if (trimmed.length === 0) return;
    toggleMutation.mutate(
      { author: trimmed, followed: false },
      { onSuccess: () => setQuery('') },
    );
  };

  const handleToggle = (author: string, followed: boolean): void => {
    toggleMutation.mutate({ author, followed });
  };

  const totalRows = filteredFollowed.length + filteredSuggestions.length;
  const isEmpty = followedAuthors.length === 0 && suggestions.length === 0;

  return (
    <div className="authors-card">
      <h3>👀 Auteurs suivis</h3>

      <AuthorAutocomplete
        value={query}
        onChange={setQuery}
        onPick={(author) => {
          toggleMutation.mutate(
            { author, followed: false },
            { onSuccess: () => setQuery('') },
          );
        }}
        onCommit={handleAddRaw}
        disabled={toggleMutation.isPending}
        excludeAuthors={followedSet}
      />

      {isEmpty ? (
        <p className="mt-3 text-sm text-brown-soft">
          Tu ne suis pas encore d'auteur. Tape un nom au-dessus pour suivre.
        </p>
      ) : totalRows === 0 ? (
        <p className="mt-3 text-sm text-brown-soft">
          Aucun auteur ne correspond à « {query} ».
        </p>
      ) : (
        <div
          className="mt-3 overflow-y-auto"
          style={{ maxHeight: SCROLL_MAX_HEIGHT }}
        >
          {filteredFollowed.map((author) => {
            const next = nextByAuthor.get(author);
            return (
              <AuthorRow
                key={`f-${author}`}
                author={author}
                title={next?.title}
                date={next?.date ?? null}
                followed
                onToggle={() => handleToggle(author, true)}
                disabled={toggleMutation.isPending}
              />
            );
          })}

          {filteredSuggestions.length > 0 && (
            <>
              {filteredFollowed.length > 0 && (
                <p className="mt-3 text-[11px] uppercase tracking-wider text-brown-light">
                  Suggestions
                </p>
              )}
              {filteredSuggestions.map((author) => {
                const next = nextByAuthor.get(author);
                return (
                  <AuthorRow
                    key={`s-${author}`}
                    author={author}
                    title={next?.title}
                    date={next?.date ?? null}
                    followed={false}
                    onToggle={() => handleToggle(author, false)}
                    disabled={toggleMutation.isPending}
                  />
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface AuthorAutocompleteProps {
  value: string;
  onChange: (v: string) => void;
  onPick: (author: string) => void;
  onCommit: () => void;
  disabled: boolean;
  excludeAuthors: ReadonlySet<string>;
}

function AuthorAutocomplete({
  value,
  onChange,
  onPick,
  onCommit,
  disabled,
  excludeAuthors,
}: AuthorAutocompleteProps): JSX.Element {
  const debouncedValue = useDebouncedValue(value, 200);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: suggestions } = useAuthorSuggestions(debouncedValue, open);
  const items = useMemo(() => {
    const list = suggestions?.items ?? [];
    return list.filter(
      (s) => !excludeAuthors.has(s.author.toLowerCase()),
    );
  }, [suggestions, excludeAuthors]);

  useEffect(() => {
    setHighlight(0);
  }, [debouncedValue]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const showDropdown =
    open && debouncedValue.trim().length >= 1 && items.length > 0;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown' && showDropdown) {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, items.length - 1));
    } else if (e.key === 'ArrowUp' && showDropdown) {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (showDropdown && items[highlight]) {
        onPick(items[highlight]!.author);
        setOpen(false);
      } else {
        onCommit();
      }
    }
  };

  return (
    <div ref={containerRef} className="mt-3">
      <div className="relative flex items-center gap-2">
        <span
          className="pointer-events-none absolute left-3 text-brown-light"
          aria-hidden
        >
          🔍
        </span>
        <input
          type="text"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Rechercher ou ajouter un auteur…"
          className="flex-1 rounded-full bg-cream-light pl-8 pr-3 py-1.5 text-xs text-brown placeholder:text-brown-light focus:outline-none focus:ring-1 focus:ring-terracotta"
        />
        <button
          type="button"
          onClick={onCommit}
          disabled={disabled || value.trim().length === 0}
          className="rounded-full bg-terracotta px-3 py-1.5 text-xs font-semibold text-cream-light hover:bg-terracotta-dark disabled:opacity-50"
        >
          Suivre
        </button>
      </div>

      {showDropdown && (
        <ul
          role="listbox"
          className="mt-2 max-h-44 overflow-y-auto rounded-2xl bg-cream-light/70 p-1 text-xs ring-1 ring-cream-dark"
        >
          {items.map((s, i) => {
            const active = i === highlight;
            return (
              <li key={s.author} role="option" aria-selected={active}>
                <button
                  type="button"
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onPick(s.author);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center justify-between rounded-xl px-2.5 py-1.5 text-left transition-colors ${
                    active ? 'bg-cream-dark text-brown' : 'text-brown hover:bg-cream-dark'
                  }`}
                >
                  <span className="truncate font-semibold">{s.author}</span>
                  <span className="ml-2 flex-shrink-0 text-[10px] text-brown-light">
                    {s.bookCount} livre{s.bookCount > 1 ? 's' : ''}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

interface AuthorRowProps {
  readonly author: string;
  readonly title?: string;
  readonly date: Date | null;
  readonly followed: boolean;
  readonly disabled: boolean;
  readonly onToggle: () => void;
}

function AuthorRow({
  author,
  title,
  date,
  followed,
  disabled,
  onToggle,
}: AuthorRowProps): JSX.Element {
  return (
    <div className="author-row">
      <span className="author-avatar">{author.charAt(0).toUpperCase()}</span>
      <div className="author-info">
        <div className="author-name">{author}</div>
        <div className="author-meta">
          {title
            ? `${title}${date ? ` — ${DATE_FORMAT.format(date)}` : ''}`
            : 'Aucune sortie annoncée'}
        </div>
      </div>
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        aria-label={followed ? `Ne plus suivre ${author}` : `Suivre ${author}`}
        title={followed ? 'Ne plus suivre' : 'Suivre'}
        className="rounded-full bg-transparent p-1 transition-opacity hover:bg-cream-dark/40 disabled:opacity-50"
      >
        <span aria-hidden style={{ opacity: followed ? 1 : 0.35 }} className="author-bell">
          🔔
        </span>
      </button>
    </div>
  );
}
